import type { Photo } from "./types";

export interface PhotoDayGroupItem {
  readonly photo: Photo;
  /** Index of the photo in the flat gallery array. The modal, deep links
   *  and usePhotoDeepLink all key off this flat index. */
  readonly index: number;
}

export interface PhotoDayGroup {
  /** Calendar day as `YYYY-MM-DD`, or null when the photos carry no date. */
  readonly day: string | null;
  readonly items: readonly PhotoDayGroupItem[];
}

const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Calendar day a photo belongs to. Prefers `localDateTime` (Immich's
 * wall-clock time at the capture location, which Immich's own timeline
 * groups by) over `fileCreatedAt` (a UTC instant). The day is sliced
 * straight off the ISO string: Immich serialises localDateTime with a `Z`
 * suffix even though it represents local wall time, so parsing it through
 * `new Date()` would shift photos across midnight in some viewer timezones.
 */
export function photoDayKey(photo: Photo): string | null {
  const iso = photo.localDateTime ?? photo.fileCreatedAt;
  const day = iso?.slice(0, 10);
  return day !== undefined && DAY_KEY_PATTERN.test(day) ? day : null;
}

/**
 * Split the flat, already-sorted photo list into consecutive same-day runs.
 * Never reorders: the flat array stays the source of truth for modal
 * navigation, so each item carries its original index.
 */
export function groupPhotosByDay(
  photos: readonly Photo[],
): readonly PhotoDayGroup[] {
  const days = photos.map(photoDayKey);
  const starts = days.flatMap((day, i) =>
    i === 0 || day !== days[i - 1] ? [i] : [],
  );
  return starts.map((start, g) => ({
    day: days[start] ?? null,
    items: photos
      .slice(start, starts[g + 1])
      .map((photo, offset) => ({ photo, index: start + offset })),
  }));
}

/**
 * Human-readable label for a `YYYY-MM-DD` day key, e.g. "zondag 12 juli
 * 2026" (nl) or "Sunday, July 12, 2026" (en). Formats the day at UTC
 * midnight in the UTC zone so the label never shifts with the viewer's
 * timezone.
 */
export function formatPhotoDayLabel(day: string, locale: string): string {
  const [year, month, dayOfMonth] = day.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "full",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, dayOfMonth)));
}
